import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col } from "reactstrap";
import { toast } from "react-toastify";
import NeoButton from "../../../components/Common/NeoButton";
import NeoAlert from "../../../components/Common/NeoAlert";
import NeoCard from "../../../components/Common/NeoCard";
import NeoBadge from "../../../components/Common/NeoBadge";
import DetailGeneral from "./components/Panels/DetailGeneral";
import DetailSubscription from "./components/Panels/DetailSubscription";
import DetailBilling from "./components/Panels/DetailBilling";
import DetailUsers from "./components/Panels/DetailUsers";
import DetailUsage from "./components/Panels/DetailUsage";
import DetailOnboarding from "./components/Panels/DetailOnboarding";
import DetailActionModals from "./components/DetailActionModals";
import DetailSupport from "./components/Panels/DetailSupport";
import DetailLogs from "./components/Panels/DetailLogs";
import DetailTimeline from "./components/Panels/DetailTimeline";
import { getTenantDetail, listAuditLogs, listPlans, changeTenantPlan, updateTenantTrialPeriod } from "../../../services/platform_admin_service";
import { formatIsoDatePtBr } from "../../../utils/tenantLifecycle";

// ── Helpers ──────────────────────────────────────────────────────────────────
const TRIAL_BILLING_STATUSES = new Set(["trial", "trial_started", "trial_active", "trialing"]);

const resolveStatus = (data) => {
    if (data.accessBlockedAt || data.active === false) return "blocked";
    if (TRIAL_BILLING_STATUSES.has(data.billingStatus)) {
        const todayISO = new Date().toLocaleDateString("en-CA");
        if (data.trialStartDate && data.trialStartDate > todayISO) return "trial_scheduled";
        if (data.trialEndsAt && data.trialEndsAt < todayISO) return "expired";
        return "trial";
    }
    if (data.billingStatus === "manual_active" || data.billingStatus === "active" || data.active) return "active";
    if (data.billingStatus === "cancelled") return "cancelled";
    return data.billingStatus || "draft";
};

const formatTrialRange = (tenant) => {
    const start = formatIsoDatePtBr(tenant.trialStartDate);
    const end = formatIsoDatePtBr(tenant.trialEndsAt);
    if (start && end) return `${start} a ${end}`;
    if (end) return `Até ${end}`;
    return null;
};

const flattenTenant = (raw) => {
    const addr = raw.address || {};
    return {
        ...raw,
        // Flatten address
        zipCode: addr.zipCode || "",
        street: addr.street || "",
        number: addr.number || "",
        complement: addr.complement || "",
        neighborhood: addr.neighborhood || "",
        city: addr.city || "",
        state: addr.state || "",
        // Flatten owner
        responsibleName: raw.owner?.fullName || raw.ownerName || "",
        responsibleEmail: raw.owner?.email || raw.ownerEmail || "",
        responsiblePhone: raw.owner?.phone || "",
        // Visual fields
        photoUrl: raw.logoUrl || null,
        status: resolveStatus(raw),
        amount: raw.commercialAmount || 0,
        dueDate: raw.nextBillingDate || null,
        billingCycle: "monthly",
    };
};

// ── Component ────────────────────────────────────────────────────────────────
const PlatformTenantDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("general");

    // Logs reais do audit
    const [logs, setLogs] = useState([]);

    // Dados que ainda não possuem backend dedicado — iniciam vazios
    const [faturas, setFaturas] = useState([]);
    const [chamados, setChamados] = useState([]);
    const [checklist, setChecklist] = useState([]);

    // Modais
    const [modalAction, setModalAction] = useState(null);
    const [tempPlan, setTempPlan] = useState("");
    const [tempTrialStartDate, setTempTrialStartDate] = useState("");
    const [tempTrialEndsAt, setTempTrialEndsAt] = useState("");
    const [platformPlans, setPlatformPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [changingPlan, setChangingPlan] = useState(false);
    const [changingTrial, setChangingTrial] = useState(false);
    const [liberateDays, setLiberateDays] = useState(3);
    const [selectedFatura, setSelectedFatura] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);

    // ── Fetch Tenant ─────────────────────────────────────────────────────────
    const fetchTenant = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getTenantDetail(id);
            const flat = flattenTenant(data);
            setTenant(flat);
            setTempPlan(flat.platformPlanId || flat.planId || "");
            setTempTrialStartDate(flat.trialStartDate || new Date().toLocaleDateString("en-CA"));
            setTempTrialEndsAt(flat.trialEndsAt || flat.dueDate || new Date().toLocaleDateString("en-CA"));
        } catch (err) {
            console.error("[Detail] Erro ao carregar academia:", err);
            toast.error(err.message || "Erro ao carregar dados da academia.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchPlatformPlans = useCallback(async () => {
        try {
            setLoadingPlans(true);
            const result = await listPlans();
            setPlatformPlans((result.items || []).filter((plan) => plan.active !== false));
        } catch (err) {
            console.error("[Detail] Erro ao carregar planos comerciais:", err);
            toast.error(err.message || "Erro ao carregar planos comerciais.");
        } finally {
            setLoadingPlans(false);
        }
    }, []);

    // ── Fetch Audit Logs for this tenant ─────────────────────────────────────
    const fetchLogs = useCallback(async () => {
        try {
            const result = await listAuditLogs({
                page: 1,
                pageSize: 50,
                filters: { targetTenantId: id }
            });
            const items = (result.items || []).map((log) => ({
                id: log.id,
                action: log.action,
                desc: log.description,
                user: log.actorName || log.actorEmail || "Sistema",
                rawDate: log.createdAt || null,
                date: log.createdAt
                    ? new Date(log.createdAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
                    : "",
            }));
            setLogs(items);
        } catch (err) {
            console.error("[Detail] Erro ao carregar logs:", err);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchTenant();
            fetchLogs();
            fetchPlatformPlans();
        }
    }, [id, fetchTenant, fetchLogs, fetchPlatformPlans]);

    // ── Local action handlers (interface interactions) ────────────────────────
    const handleLogAction = (actionKey, descText, user = "Admin") => {
        const newLog = {
            id: Date.now(),
            action: actionKey,
            desc: descText,
            user: user,
            date: new Date().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }),
        };
        setLogs((prev) => [newLog, ...prev]);
    };

    const handlePlanChange = async () => {
        if (!tempPlan) {
            toast.warning("Selecione um plano comercial.");
            return;
        }

        const oldPlan = tenant.platformPlanTitle || tenant.planId;
        const selectedPlan = platformPlans.find((plan) => plan.id === tempPlan);

        try {
            setChangingPlan(true);
            await changeTenantPlan(tenant.id, tempPlan);
            setTenant((prev) => ({
                ...prev,
                planId: selectedPlan?.basePlanId || selectedPlan?.planId || prev.planId,
                platformPlanId: tempPlan,
                platformPlanTitle: selectedPlan?.title || tempPlan,
                amount: selectedPlan?.price ?? selectedPlan?.amount ?? prev.amount,
                effectiveFeatures: selectedPlan?.effectiveFeatures || prev.effectiveFeatures,
            }));
            handleLogAction("subscription_plan_changed", `Plano comercial alterado de ${oldPlan} para ${selectedPlan?.title || tempPlan}`);
            setAlertMessage({ type: "success", text: `Plano alterado para ${selectedPlan?.title || tempPlan} com sucesso!` });
            setModalAction(null);
            await fetchTenant();
        } catch (err) {
            console.error("[Detail] Erro ao alterar plano:", err);
            toast.error(err.message || "Erro ao alterar plano comercial.");
        } finally {
            setChangingPlan(false);
        }
    };

    const handleTrialChange = async () => {
        if (!tempTrialStartDate || !tempTrialEndsAt) {
            toast.warning("Informe a data inicial e final do trial.");
            return;
        }

        if (tempTrialEndsAt < tempTrialStartDate) {
            toast.warning("A data final do trial deve ser igual ou posterior à data inicial.");
            return;
        }

        try {
            setChangingTrial(true);
            await updateTenantTrialPeriod(tenant.id, tempTrialStartDate, tempTrialEndsAt);
            const todayISO = new Date().toLocaleDateString("en-CA");
            setTenant((prev) => ({
                ...prev,
                active: true,
                billingStatus: "trial_started",
                status: tempTrialStartDate > todayISO ? "trial_scheduled" : "trial",
                trialStartDate: tempTrialStartDate,
                trialEndsAt: tempTrialEndsAt,
                dueDate: tempTrialEndsAt,
            }));
            handleLogAction("trial_period_updated", `Alterou o trial para ${formatIsoDatePtBr(tempTrialStartDate)} a ${formatIsoDatePtBr(tempTrialEndsAt)}`);
            setAlertMessage({ type: "success", text: "Período de trial atualizado com sucesso!" });
            setModalAction(null);
            await fetchTenant();
        } catch (err) {
            console.error("[Detail] Erro ao alterar trial:", err);
            toast.error(err.message || "Erro ao alterar período de trial.");
        } finally {
            setChangingTrial(false);
        }
    };

    const handleBlockToggle = () => {
        const newStatus = tenant.status === "blocked" ? "active" : "blocked";
        setTenant((prev) => ({ ...prev, status: newStatus }));
        handleLogAction(
            "tenant_blocked",
            newStatus === "blocked" ? "Acesso da academia bloqueado por inadimplência" : "Acesso da academia liberado oficialmente"
        );
        setAlertMessage({
            type: newStatus === "blocked" ? "danger" : "success",
            text: newStatus === "blocked" ? "Academia bloqueada com sucesso!" : "Academia reativada com sucesso!",
        });
        setModalAction(null);
    };

    const handleLiberateTemporarily = () => {
        setTenant((prev) => ({ ...prev, status: "active" }));
        handleLogAction("tenant_temporary_liberation", `Acesso liberado temporariamente por ${liberateDays} dias`);
        setAlertMessage({ type: "success", text: `Acesso estendido por ${liberateDays} dias com sucesso!` });
        setModalAction(null);
    };

    const handleImpersonate = () => {
        handleLogAction("admin_impersonation", `Acessou o painel da academia como administrador interno`);
        setAlertMessage({
            type: "info",
            text: "Simulação de Impersonate iniciada! Redirecionando para o painel de administração da academia...",
        });
        setModalAction(null);
        setTimeout(() => { navigate("/dashboard"); }, 1800);
    };

    const handlePayFatura = (faturaId) => {
        setFaturas(
            faturas.map((f) => {
                if (f.id === faturaId) {
                    return { ...f, status: "pago", paidAt: new Date().toISOString().replace("T", " ").substring(0, 16) };
                }
                return f;
            })
        );
        handleLogAction("payment_registered_manual", `Registrou pagamento manual da fatura ${faturaId}`);
        setAlertMessage({ type: "success", text: `Fatura ${faturaId} marcada como PAGA.` });
        setModalAction(null);
    };

    const handleToggleChecklist = (checkId) => {
        setChecklist(
            checklist.map((item) => {
                if (item.id === checkId) {
                    const nextState = !item.checked;
                    handleLogAction(
                        "onboarding_step_updated",
                        `Etapa do Onboarding '${item.label}' marcada como ${nextState ? "Concluída" : "Pendente"}`
                    );
                    return { ...item, checked: nextState };
                }
                return item;
            })
        );
    };

    // ── Financial summary ────────────────────────────────────────────────────
    const tenantTotalPagas = faturas.filter((f) => f.status === "pago").reduce((acc, f) => acc + f.amount, 0);
    const tenantTotalPendentes = faturas.filter((f) => f.status === "pending").reduce((acc, f) => acc + f.amount, 0);
    const tenantSummaryData = [
        {
            label: "Total Pago",
            value: `R$ ${tenantTotalPagas.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
            count: faturas.filter((f) => f.status === "pago").length,
            icon: "mdi-check-circle-outline",
            color: "#0ab39c",
            bg: "rgba(10, 179, 156, 0.1)",
        },
        {
            label: "Total Pendente",
            value: `R$ ${tenantTotalPendentes.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
            count: faturas.filter((f) => f.status === "pending").length,
            icon: "mdi-clock-outline",
            color: "#2563eb",
            bg: "rgba(37, 99, 235, 0.1)",
        },
    ];
    const trialRange = tenant ? formatTrialRange(tenant) : null;

    // ── Users list from owner data ───────────────────────────────────────────
    const schoolUsers = tenant?.owner
        ? [
              {
                  name: tenant.owner.fullName || tenant.responsibleName || "—",
                  email: tenant.owner.email || tenant.responsibleEmail || "—",
                  role: "Owner",
                  status: tenant.owner.status || "Ativo",
                  lastLogin: "—",
              },
          ]
        : [];

    // ── Loading state ────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="pb-5">
                <NeoButton variant="soft-secondary" size="sm" className="mb-4" onClick={() => navigate("/platform/tenants")}>
                    <i className="ph-light ph-arrow-left me-1" /> Voltar para Academias
                </NeoButton>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <p className="text-muted mt-3">Carregando dados da academia...</p>
                </div>
            </div>
        );
    }

    if (!tenant) {
        return (
            <div className="pb-5">
                <NeoButton variant="soft-secondary" size="sm" className="mb-4" onClick={() => navigate("/platform/tenants")}>
                    <i className="ph-light ph-arrow-left me-1" /> Voltar para Academias
                </NeoButton>
                <NeoAlert color="danger">Academia não encontrada.</NeoAlert>
            </div>
        );
    }

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="pb-5">
            {/* Botão Voltar */}
            <NeoButton variant="soft-secondary" size="sm" className="mb-4" onClick={() => navigate("/platform/tenants")}>
                <i className="ph-light ph-arrow-left me-1" /> Voltar para Academias
            </NeoButton>

            {/* Toast de Alerta Interno */}
            {alertMessage && (
                <NeoAlert color={alertMessage.type} toggle={() => setAlertMessage(null)} className="mb-4 shadow-sm border-0 py-2 px-3">
                    {alertMessage.text}
                </NeoAlert>
            )}

            {/* Grid Principal */}
            <Row>
                {/* Lateral Esquerda - Resumo */}
                <Col lg={3} md={4} className="mb-4">
                    <NeoCard thinBorder={true} className="overflow-hidden" bodyClassName="p-0">
                        {/* Capa com Gradiente Premium */}
                        <div className="neo-detail-cover" />

                        {/* Conteúdo com Padding */}
                        <div className="neo-detail-avatar-container">
                            {tenant.photoUrl ? (
                                <img
                                    src={tenant.photoUrl}
                                    alt={tenant.name}
                                    className="neo-detail-avatar-img mb-3"
                                />
                            ) : (
                                <div className="neo-detail-avatar-placeholder">
                                    {(tenant.name || "??").substring(0, 2).toUpperCase()}
                                </div>
                            )}
                            <h5 className="m-0 fw-bold text-dark mb-1">{tenant.name}</h5>
                            <span className="text-xs text-muted d-block mb-3">
                                {TRIAL_BILLING_STATUSES.has(tenant.billingStatus)
                                    ? `Período de teste${trialRange ? ` — ${trialRange}` : ""}`
                                    : "Contrato SaaS ativo"}
                            </span>
                            
                            <div className="mb-4">
                                <NeoBadge
                                    color={
                                        tenant.status === "active"
                                            ? "success"
                                            : tenant.status === "trial"
                                            ? "info"
                                            : tenant.status === "trial_scheduled"
                                            ? "warning"
                                            : tenant.status === "blocked"
                                            ? "danger"
                                            : "muted"
                                    }
                                >
                                    {tenant.status === "active"
                                        ? "Ativo"
                                        : tenant.status === "trial"
                                        ? "Em Teste"
                                        : tenant.status === "trial_scheduled"
                                        ? "Trial Agendado"
                                        : tenant.status === "blocked"
                                        ? "Bloqueado"
                                        : tenant.status === "expired"
                                        ? "Expirado"
                                        : "Cancelado"}
                                </NeoBadge>
                            </div>

                            {/* Itens Cadastrais em Blocos Premium */}
                            <div className="d-flex flex-column gap-2 text-start mb-4">
                                {/* Bloco 1: Plano */}
                                <div className="neo-detail-block-item">
                                    <div className="neo-detail-block-icon">
                                        <i className="mdi mdi-certificate-outline fs-5" />
                                    </div>
                                    <div className="flex-grow-1">
                                        <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Plano Comercial</span>
                                        <span className="fw-bold text-dark font-size-13">{tenant.platformPlanTitle || tenant.planId || "—"}</span>
                                        {tenant.amount > 0 && (
                                            <span className="text-xs text-muted d-block mt-0.5">
                                                R$ {Number(tenant.amount).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} / Mensal
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Bloco 2: Vencimento */}
                                {tenant.dueDate && (
                                    <div className="neo-detail-block-item">
                                        <div className="neo-detail-block-icon">
                                            <i className="mdi mdi-calendar-outline fs-5" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Próximo Vencimento</span>
                                            <span className="fw-bold text-dark font-size-13">{new Date(tenant.dueDate + "T12:00:00").toLocaleDateString("pt-BR")}</span>
                                        </div>
                                    </div>
                                )}

                                {TRIAL_BILLING_STATUSES.has(tenant.billingStatus) && trialRange && (
                                    <div className="neo-detail-block-item">
                                        <div className="neo-detail-block-icon">
                                            <i className="mdi mdi-calendar-clock-outline fs-5" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Período de Teste</span>
                                            <span className="fw-bold text-dark font-size-13">{trialRange}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Bloco 3: Responsável */}
                                <div className="neo-detail-block-item">
                                    <div className="neo-detail-block-icon">
                                        <i className="mdi mdi-account-tie-outline fs-5" />
                                    </div>
                                    <div className="flex-grow-1">
                                        <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Responsável Principal</span>
                                        <span className="fw-bold text-dark font-size-13">{tenant.responsibleName || "—"}</span>
                                    </div>
                                </div>

                                {/* Bloco 4: Telefone */}
                                {tenant.phone && (
                                    <div className="neo-detail-block-item">
                                        <div className="neo-detail-block-icon">
                                            <i className="mdi mdi-whatsapp fs-5" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>WhatsApp Financeiro</span>
                                            <span className="fw-bold text-dark font-size-13">{tenant.phone}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Bloco 5: Localidade */}
                                {(tenant.city || tenant.state) && (
                                    <div className="neo-detail-block-item">
                                        <div className="neo-detail-block-icon">
                                            <i className="mdi mdi-map-marker-outline fs-5" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Localidade</span>
                                            <span className="fw-bold text-dark font-size-13">{[tenant.city, tenant.state].filter(Boolean).join(" - ")}</span>
                                            {tenant.zipCode && <span className="text-xs text-muted d-block mt-0.5">{tenant.zipCode}</span>}
                                        </div>
                                    </div>
                                )}

                                {/* Bloco 6: Criado em */}
                                {tenant.createdAt && (
                                    <div className="neo-detail-block-item">
                                        <div className="neo-detail-block-icon">
                                            <i className="mdi mdi-clock-plus-outline fs-5" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Cadastrado em</span>
                                            <span className="fw-bold text-dark font-size-13">
                                                {new Date(tenant.createdAt).toLocaleDateString("pt-BR")}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Botões de Ação na Lateral */}
                            <div className="neo-detail-actions-divider pt-3">
                                <NeoButton 
                                    variant="soft-primary" 
                                    className="w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                                    onClick={() => setModalAction("impersonate")}
                                >
                                    <i className="ph-light ph-sign-in fs-5" /> Entrar como Cliente
                                </NeoButton>
                                
                                {tenant.status === "blocked" ? (
                                    <>
                                        <NeoButton 
                                            variant="success" 
                                            className="w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                                            onClick={() => setModalAction("block")}
                                        >
                                            <i className="ph-light ph-lock-key-open fs-5" /> Reativar Acesso
                                        </NeoButton>
                                        <NeoButton 
                                            variant="primary" 
                                            className="w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                                            onClick={() => setModalAction("temp_liberate")}
                                        >
                                            <i className="ph-light ph-clock fs-5" /> Liberar Temporariamente
                                        </NeoButton>
                                    </>
                                ) : (
                                    <NeoButton 
                                        variant="outline-danger" 
                                        className="w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                                        onClick={() => setModalAction("block")}
                                    >
                                        <i className="ph-light ph-lock fs-5" /> Bloquear Academia
                                    </NeoButton>
                                )}
                            </div>
                        </div>
                    </NeoCard>
                </Col>

                {/* Área Direita - Abas */}
                <Col lg={9} md={8}>
                    <div className="d-flex gap-2 border-bottom overflow-x-auto pb-1 mb-4 status-tabs-nav">
                        <button className={`status-tab-link ${activeTab === "general" ? "active" : ""}`} onClick={() => setActiveTab("general")}>Dados Gerais</button>
                        <button className={`status-tab-link ${activeTab === "subscription" ? "active" : ""}`} onClick={() => setActiveTab("subscription")}>Assinatura</button>
                        <button className={`status-tab-link ${activeTab === "billing" ? "active" : ""}`} onClick={() => setActiveTab("billing")}>Pagamentos / Faturas</button>
                        <button className={`status-tab-link ${activeTab === "users" ? "active" : ""}`} onClick={() => setActiveTab("users")}>Usuários</button>
                        <button className={`status-tab-link ${activeTab === "usage" ? "active" : ""}`} onClick={() => setActiveTab("usage")}>Uso do Sistema</button>
                        <button className={`status-tab-link ${activeTab === "onboarding" ? "active" : ""}`} onClick={() => setActiveTab("onboarding")}>Onboarding / Implantação</button>
                        <button className={`status-tab-link ${activeTab === "support" ? "active" : ""}`} onClick={() => setActiveTab("support")}>Suporte</button>
                        <button className={`status-tab-link ${activeTab === "timeline" ? "active" : ""}`} onClick={() => setActiveTab("timeline")}>Timeline</button>
                        <button className={`status-tab-link ${activeTab === "logs" ? "active" : ""}`} onClick={() => setActiveTab("logs")}>Histórico de Logs</button>
                    </div>

                    {/* Conteúdo das Abas */}
                    <div className="mt-2">
                        {activeTab === "general" && (
                            <DetailGeneral
                                tenant={tenant}
                                setTenant={setTenant}
                                handleLogAction={handleLogAction}
                                setAlertMessage={setAlertMessage}
                            />
                        )}

                        {activeTab === "subscription" && (
                            <DetailSubscription
                                tenant={tenant}
                                setTenant={setTenant}
                                handleLogAction={handleLogAction}
                                onAlterPlan={() => setModalAction("plan")}
                                onAlterTrial={() => setModalAction("trial")}
                            />
                        )}

                        {activeTab === "billing" && (
                            <DetailBilling
                                tenant={tenant}
                                faturas={faturas}
                                setFaturas={setFaturas}
                                handleLogAction={handleLogAction}
                                tenantSummaryData={tenantSummaryData}
                                onRegisterPayment={(row) => {
                                    setSelectedFatura(row);
                                    setModalAction("payment");
                                }}
                            />
                        )}

                        {activeTab === "users" && (
                            <DetailUsers schoolUsers={schoolUsers} />
                        )}

                        {activeTab === "usage" && (
                            <DetailUsage tenant={tenant} />
                        )}

                        {activeTab === "onboarding" && (
                            <DetailOnboarding
                                checklist={checklist}
                                onToggleChecklist={handleToggleChecklist}
                            />
                        )}

                        {activeTab === "support" && (
                            <DetailSupport
                                chamados={chamados}
                                setChamados={setChamados}
                                handleLogAction={handleLogAction}
                            />
                        )}

                        {activeTab === "timeline" && (
                            <DetailTimeline tenant={tenant} logs={logs} />
                        )}

                        {activeTab === "logs" && (
                            <DetailLogs logs={logs} />
                        )}
                    </div>
                </Col>
            </Row>

            {/* Modais de Ação do Tenant */}
            <DetailActionModals
                modalAction={modalAction}
                onClose={() => setModalAction(null)}
                tenant={tenant}
                tempPlan={tempPlan}
                tempTrialStartDate={tempTrialStartDate}
                tempTrialEndsAt={tempTrialEndsAt}
                platformPlans={platformPlans}
                loadingPlans={loadingPlans}
                changingPlan={changingPlan}
                changingTrial={changingTrial}
                onChangeTempPlan={setTempPlan}
                onChangeTempTrialStartDate={setTempTrialStartDate}
                onChangeTempTrialEndsAt={setTempTrialEndsAt}
                onPlanChange={handlePlanChange}
                onTrialChange={handleTrialChange}
                onBlockToggle={handleBlockToggle}
                liberateDays={liberateDays}
                onChangeLiberateDays={setLiberateDays}
                onLiberateTemporarily={handleLiberateTemporarily}
                onImpersonate={handleImpersonate}
                selectedFatura={selectedFatura}
                onPayFatura={handlePayFatura}
            />
        </div>
    );
};

export default PlatformTenantDetail;
